import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, TextField, Checkbox, ListItemText,
  MenuItem, Select, OutlinedInput, InputLabel, FormControl,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { schedulesService, ScheduleItem, ScheduleProfessionalOption } from '../services/schedulesService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { scheduleBlocksService, ScheduleBlockItem } from '../services/scheduleBlocksService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';

/**
 * Impressão da Agenda (RN-120): um bloco por profissional, cada um começando em página nova.
 *
 * A tela chega com o recorte da agenda (janela de datas e profissionais marcados) e permite
 * apertar esse recorte antes de imprimir, sem voltar: as duas datas e a seleção de
 * profissionais, que abre com TODOS os que vieram filtrados. O dropdown é o mesmo da agenda —
 * caixas de seleção com "Todos" — para que a escolha se faça do mesmo jeito nos dois lugares.
 *
 * Cabeçalho e rodapé vêm do TEMPLATE PADRÃO da entidade e se repetem em todas as páginas por
 * thead/tfoot (RN-534e).
 */
const formatarData = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const horaDe = (iso: string): string => iso.slice(11, 16);

/** Agendamento sem confirmacao ou sem presenca registrada — enquanto a data nao chega, e a
 *  maioria. Precisa de valor proprio no filtro, senao sai da folha sem o usuario perceber. */
const SEM_STATUS = '__sem__';

const rotuloStatus = (valor: string): string => (valor === SEM_STATUS ? 'Sem registro' : valor);

/** Data local no formato yyyy-MM-dd — o mesmo recorte que a agenda manda para a API. */
const hojeLocal = (): string => {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return d.getFullYear() + '-' + mes + '-' + dia;
};

const SchedulePrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();

  // O dia corrente é o padrão; a agenda pode chegar com outra janela nos parâmetros
  const [fromDate, setFromDate] = useState(getParam('from') || hojeLocal());
  const [toDate, setToDate] = useState(getParam('to') || hojeLocal());

  const professionalsParam = getParam('professionals') || '';
  /**
   * Duas impressoes da mesma agenda (RN-120e):
   * - `por-profissional`: uma folha por profissional, para entregar a cada um;
   * - `corrido`: tudo numa sequencia so, para a coordenacao ler junto sem gastar 19 folhas.
   * Vem na URL, e da para trocar aqui mesmo antes de imprimir.
   */
  const [layout, setLayout] = useState<'por-profissional' | 'corrido'>(
    getParam('layout') === 'corrido' ? 'corrido' : 'por-profissional');
  const teamFilter = getParam('team') || '';

  const [allProfessionals, setAllProfessionals] = useState<ScheduleProfessionalOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(
    professionalsParam ? professionalsParam.split(',').filter(Boolean) : [],
  );
  /**
   * Recorte por situacao (RN-120f): confirmacao e presenca sao catalogos distintos e o papel
   * costuma sair por um deles — a folha do repasse quer so quem compareceu, a ligacao do dia
   * seguinte quer so quem nao confirmou. Como todo filtro da impressao, este so aperta
   * (RN-120b): abre com tudo marcado, que e o mesmo recorte que a agenda mostrava.
   */
  const [confirmationNames, setConfirmationNames] = useState<string[]>([]);
  const [presenceNames, setPresenceNames] = useState<string[]>([]);
  const [selectedConfirmations, setSelectedConfirmations] = useState<string[]>([]);
  const [selectedPresences, setSelectedPresences] = useState<string[]>([]);

  /* Compromisso SEM paciente e bloqueio de agenda -- reuniao, almoco, horario fechado. Conta
     como linha na folha e nao e atendimento, entao a coordenacao quer poder tira-lo (pedido do
     Hilton, 11/09/2026). "Sem paciente" isolado serve para o contrario: conferir os bloqueios. */
  const [patientFilter, setPatientFilter] = useState<'todos' | 'definido' | 'sem'>('todos');

  /* Bloqueio de agenda (RN-118n): marcação sem paciente, em tabela própria. Entra na folha na
     ordem do horário, com filtro próprio — os filtros de situação não se aplicam a ele. */
  const [blocksFilter, setBlocksFilter] = useState<'mostrar' | 'ocultar'>('mostrar');
  const [blocks, setBlocks] = useState<ScheduleBlockItem[]>([]);

  const [items, setItems] = useState<ScheduleItem[]>([]);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'schedule');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão da Agenda';
  }, []);

  // Catálogo e template: uma vez só. A lista oferecida é a que veio filtrada da agenda —
  // imprimir não é a hora de descobrir profissionais que a tela não estava mostrando.
  useEffect(() => {
    const load = async () => {
      try {
        const [options, templates] = await Promise.all([
          schedulesService.getOptions(),
          user?.entityId
            ? printTemplateService.getByEntity(user.entityId).catch(() => [])
            : Promise.resolve<PrintTemplate[]>([]),
        ]);

        const ativos = options.professionals.filter(p => p.active);
        const doFiltro = professionalsParam
          ? ativos.filter(p => professionalsParam.split(',').includes(p.id))
          : ativos;

        const confirmacoes = [...(options.confirmationStatuses ?? [])]
          .sort((a, z) => a.order - z.order).map(st => st.name).concat(SEM_STATUS);
        const presencas = [...(options.presenceStatuses ?? [])]
          .sort((a, z) => a.order - z.order).map(st => st.name).concat(SEM_STATUS);

        setAllProfessionals(doFiltro);
        setSelectedIds(prev => (prev.length > 0 ? prev : doFiltro.map(p => p.id)));
        setConfirmationNames(confirmacoes);
        setSelectedConfirmations(confirmacoes);
        setPresenceNames(presencas);
        setSelectedPresences(presencas);
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar os dados da agenda.');
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.entityId]);

  const carregar = useCallback(async () => {
    if (selectedIds.length === 0) {
      setItems([]);
      setBlocks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [result, blockItems] = await Promise.all([
        schedulesService.getSchedules({
          from: fromDate,
          to: toDate,
          professionals: selectedIds.join(','),
        }),
        // Melhor esforço: sem bloqueios a folha sai como antes
        scheduleBlocksService.getBlocksInRange(fromDate, toDate, selectedIds.join(','))
          .catch(() => [] as ScheduleBlockItem[]),
      ]);
      setBlocks(blockItems);
      // Equipe: a agenda filtra no cliente e a impressão repete a regra, senão o papel
      // traria agendamentos que a tela não mostrava
      setItems(teamFilter
        ? result.items.filter(i => i.teamName === teamFilter)
        : result.items);
      setError('');
    } catch {
      setError('Não foi possível carregar os agendamentos.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedIds, teamFilter]);

  useEffect(() => {
    if (allProfessionals.length > 0) carregar();
  }, [carregar, allProfessionals.length]);

  /* Situacao filtra no NAVEGADOR, como a equipe (RN-120d): a consulta da agenda e a mesma
     dos outros filtros e nao conhece esse recorte. Tudo marcado = nao filtra nada, inclusive
     o que estiver fora do catalogo (status legado). */
  const statusPassa = useCallback(
    (valor: string | null, selecionados: string[], catalogo: string[]): boolean =>
      catalogo.length === 0
      || selecionados.length === catalogo.length
      || selecionados.includes(valor || SEM_STATUS),
    []);

  const itensFiltrados = useMemo(
    () => items.filter(i => {
      const temPaciente = Boolean(i.patientId || i.patientName);
      if (patientFilter === 'definido' && !temPaciente) return false;
      if (patientFilter === 'sem' && temPaciente) return false;
      return statusPassa(i.patientConfirmation || i.statusDisplay, selectedConfirmations, confirmationNames)
        && statusPassa(i.patientPresence, selectedPresences, presenceNames);
    }),
    [items, statusPassa, selectedConfirmations, confirmationNames, selectedPresences, presenceNames,
      patientFilter]);

  /** Um bloco por profissional, na ordem do nome; sem agendamento nem bloqueio, sem bloco.
   *  Bloqueio e agendamento dividem a mesma lista, na ordem do horário. */
  const blocos = useMemo(() => {
    const porProfissional = new Map<string, { name: string; items: ScheduleItem[]; blocks: ScheduleBlockItem[] }>();
    const secao = (id: string, name: string) => {
      if (!porProfissional.has(id)) {
        porProfissional.set(id, { name, items: [], blocks: [] });
      }
      return porProfissional.get(id)!;
    };

    for (const item of itensFiltrados) {
      secao(item.professionalId || 'sem-profissional', item.professionalName || 'Sem profissional').items.push(item);
    }

    if (blocksFilter === 'mostrar') {
      const nomes = new Map(allProfessionals.map(p => [p.id, p.name]));
      for (const b of blocks) {
        if (selectedIds.includes(b.professionalRegistrationId)) {
          secao(b.professionalRegistrationId, nomes.get(b.professionalRegistrationId) || 'Profissional').blocks.push(b);
        }
      }
    }

    return Array.from(porProfissional.values())
      .map(b => ({
        ...b,
        linhas: [
          ...b.items.map(item => ({ kind: 'item' as const, start: item.dateTimeInitial, item })),
          ...b.blocks.map(block => ({ kind: 'block' as const, start: block.dateTimeInitial, block })),
        ].sort((a, z) => a.start.localeCompare(z.start)),
      }))
      .sort((a, z) => a.name.localeCompare(z.name, 'pt-BR'));
  }, [itensFiltrados, blocks, blocksFilter, allProfessionals, selectedIds]);

  const alternarProfissional = (id: string) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]));
  };

  const alternarTodos = () => {
    setSelectedIds(prev =>
      prev.length === allProfessionals.length ? [] : allProfessionals.map(p => p.id));
  };

  const alternarStatus = (
    setter: React.Dispatch<React.SetStateAction<string[]>>, valor: string,
  ) => setter(prev => (prev.includes(valor) ? prev.filter(v => v !== valor) : [...prev, valor]));

  const alternarTodosStatus = (
    setter: React.Dispatch<React.SetStateAction<string[]>>, catalogo: string[],
  ) => setter(prev => (prev.length === catalogo.length ? [] : [...catalogo]));

  const resumoSelecao = (selecionados: string[], catalogo: string[]): string => {
    if (catalogo.length > 0 && selecionados.length === catalogo.length) return 'Todos';
    if (selecionados.length === 0) return 'Nenhum selecionado';
    if (selecionados.length <= 2) return selecionados.map(rotuloStatus).join(', ');
    return selecionados.length + ' selecionados';
  };

  /* O que foi apertado precisa aparecer NO PAPEL: a folha circula sozinha, e uma lista
     recortada sem dizer o recorte vira numero errado na mao de quem a recebe. */
  const rotuloPaciente: Record<'todos' | 'definido' | 'sem', string> = {
    todos: 'Todos', definido: 'Definido', sem: 'Sem paciente',
  };

  const recorteAplicado = (selecionados: string[], catalogo: string[]): string | null =>
    (catalogo.length === 0 || selecionados.length === catalogo.length
      ? null
      : (selecionados.length === 0 ? 'nenhuma' : selecionados.map(rotuloStatus).join(', ')));

  /* Cabecalho e rodape da CLINICA: no papel eles se repetem por pagina via thead/tfoot
     (RN-534e). Viraram funcao porque os dois layouts usam os mesmos — no corrido uma vez,
     no por-profissional uma vez por moldura. */
  const cabecalhoClinica = () => template && (
    <div className="print-frame-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6mm' }}>
        <div style={{ flexShrink: 0 }}>
          {template?.file ? (
            <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
          ) : null}
        </div>
        <div
          style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
        />
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
    </div>
  );

  const rodapeClinica = () => template && (
    <div
      style={{ textAlign: 'center', fontSize: '9pt', paddingTop: '6mm' }}
      dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
    />
  );

  const celulaCabecalho: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc', background: '#f1f3f5',
    fontWeight: 600, textAlign: 'left',
  };
  const celula: React.CSSProperties = {
    padding: '2mm 3mm', border: '1px solid #ccc',
    // com o layout fixo, nome longo quebra dentro da celula em vez de alargar a coluna
    overflowWrap: 'anywhere',
  };
  /* Data e horario na MESMA coluna, um sobre o outro (pedido do Hilton, 11/09/2026): as duas
     colunas separadas gastavam ~48mm de uma folha de 180mm para dois dados curtos, e ainda
     assim quebravam. Empilhados cabem em 28mm, e os ~20mm que sobram vao para nome do
     paciente e servico, que sao os textos que realmente precisam de largura. */
  const celulaData: React.CSSProperties = { ...celula, whiteSpace: 'nowrap', overflowWrap: 'normal' };

  const linhaBloqueio = (b: ScheduleBlockItem) => (
    <tr key={`b-${b.id}`} style={{ background: '#f8f9fa' }}>
    <td style={celulaData}>
      <div>{formatarData(b.dateTimeInitial)}</div>
      <div style={{ fontSize: '9pt', color: '#495057' }}>
        {horaDe(b.dateTimeInitial)} – {horaDe(b.dateTimeFinal)}
      </div>
    </td>
    <td style={celula} colSpan={2}>
      <div style={{ fontWeight: 600 }}>Bloqueio: {b.title}</div>
      {b.notes && <div style={{ fontSize: '9pt', color: '#495057' }}>{b.notes}</div>}
    </td>
    <td style={{ ...celula, color: '#6c757d' }} colSpan={2}>não é atendimento</td>
  </tr>
  );

  const linhaAgendamento = (item: ScheduleItem) => (
    <tr key={item.id}>
    <td style={celulaData}>
      <div>{formatarData(item.dateTimeInitial)}</div>
      <div style={{ fontSize: '9pt', color: '#495057' }}>
        {horaDe(item.dateTimeInitial)} – {horaDe(item.dateTimeFinal)}
      </div>
    </td>
    <td style={celula}>{item.patientName || '—'}</td>
    <td style={celula}>{item.serviceTypeName || '—'}</td>
    <td style={celula}>{item.patientConfirmation || item.statusDisplay || '—'}</td>
    <td style={celula}>{item.patientPresence || '—'}</td>
  </tr>
  );

  type Secao = (typeof blocos)[number];

  const secaoProfissional = (bloco: Secao) => (
    <>
      <Box sx={{ mb: '4mm' }}>
        <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
          Agenda Profissional
        </Typography>
        <Typography sx={{ fontSize: '11pt', color: '#212529', fontWeight: 600 }}>
          {bloco.name}
        </Typography>
        <Typography sx={{ fontSize: '10pt', color: '#495057' }}>
          {formatarData(fromDate)} a {formatarData(toDate)}
          {' · '}
          {bloco.items.length} agendamento{bloco.items.length === 1 ? '' : 's'}
          {bloco.blocks.length > 0 && ` · ${bloco.blocks.length} bloqueio${bloco.blocks.length === 1 ? '' : 's'}`}
        </Typography>
        {(recorteAplicado(selectedConfirmations, confirmationNames)
          || recorteAplicado(selectedPresences, presenceNames)
          || patientFilter !== 'todos' || blocksFilter === 'ocultar') && (
          <Typography sx={{ fontSize: '9pt', color: '#495057' }}>
            {[
              recorteAplicado(selectedConfirmations, confirmationNames)
                ? 'Confirmacao: ' + recorteAplicado(selectedConfirmations, confirmationNames)
                : null,
              recorteAplicado(selectedPresences, presenceNames)
                ? 'Presenca: ' + recorteAplicado(selectedPresences, presenceNames)
                : null,
              patientFilter !== 'todos' ? 'Paciente: ' + rotuloPaciente[patientFilter] : null,
              blocksFilter === 'ocultar' ? 'Sem bloqueios de agenda' : null,
            ].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Box>

      <table style={{
        width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse',
        fontSize: '10pt', marginBottom: '4mm',
      }}>
        <thead>
          <tr>
            {/* Com duas colunas de situacao em vez de uma, as larguras fixas encolheram:
                o que sobra e de Paciente e Servico, que sao os textos longos. */}
            {/* 30mm: o `nowrap` da celula nao quebra, entao largura curta TRANSBORDA em vez de
                quebrar. A folga garante as duas linhas inteiras -- nunca uma terceira. */}
            <th style={{ ...celulaCabecalho, width: '30mm' }}>Data</th>
            <th style={celulaCabecalho}>Paciente</th>
            <th style={celulaCabecalho}>Serviço</th>
            <th style={{ ...celulaCabecalho, width: '28mm' }}>Confirmação</th>
            <th style={{ ...celulaCabecalho, width: '26mm' }}>Presença</th>
          </tr>
        </thead>
        <tbody>
          {bloco.linhas.map(linha => (linha.kind === 'block' ? linhaBloqueio(linha.block) : linhaAgendamento(linha.item)))}
        </tbody>
      </table>
    </>
  );

  const emitidoEm = () => (
    <Typography sx={{ fontSize: '9pt', color: '#6c757d' }}>
      Emitido em {new Date().toLocaleDateString('pt-BR')}
    </Typography>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: { xs: '0.75rem', sm: '2rem' },
      '@media print': { padding: 0, backgroundColor: '#fff' },
    }}>
      <style>{`
        /* Na tela, a moldura de cada profissional e uma folha: precisa respirar entre elas.
           Na impressao a quebra de pagina ja separa, entao a margem se anula la embaixo. */
        .print-frame { width: 100%; border-collapse: collapse; }
        .print-frame + .print-frame { margin-top: 10mm; }
        .print-frame > thead { display: table-header-group; }
        .print-frame > tfoot { display: table-footer-group; }

        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .schedule-print-page {
            padding: 0 !important;
            width: auto !important;
            min-height: auto !important;
            box-shadow: none !important;
          }
          .print-frame { width: 100%; border-collapse: collapse; }
          .print-frame > thead { display: table-header-group; }
          .print-frame > tfoot { display: table-footer-group; }
          .print-frame { min-height: 262mm; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td,
          .print-frame > tfoot > tr > td { padding: 0; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td { vertical-align: top; }
          .print-frame > tfoot > tr > td { vertical-align: bottom; }
          .print-frame-header img { max-height: 18mm !important; }
          /* Cada profissional comeca em folha nova, menos o primeiro. A regra vale na
             TABELA, nao num bloco dentro da celula: quebra forcada dentro de td e
             inconsistente entre navegadores. page-break-before fica como reserva para
             quem ainda nao entende break-before. No modo corrido existe uma tabela so,
             entao esta regra simplesmente nao encontra par e nada quebra. */
          .print-frame + .print-frame {
            break-before: page;
            page-break-before: always;
            margin-top: 0;
          }
          tr { break-inside: avoid; }
          /* No corrido, a secao de um profissional pode ser maior que a folha: se ela
             tentasse nao quebrar, o navegador jogaria a folha inteira fora e deixaria um
             vao enorme. So as linhas de agendamento e que nao se partem. */
          .print-frame > tbody > tr { break-inside: auto; }
        }
      `}</style>

      {/* Filtros da impressão: somem no papel */}
      <Box
        className="c4u-filters__row"
        /* Mesma largura da FOLHA e o mesmo centro (pedido do Hilton, 11/09/2026): a pagina e
           uma previa de impressao, e uma barra mais larga que o papel flutua fora dele. Cabe-la
           numa linha so exigiria largura de monitor grande -- em tela menor voltaria a quebrar,
           e aparecer desalinhada por cima. */
        sx={{ maxWidth: '210mm', mx: 'auto', mb: 2, '@media print': { display: 'none' } }}
      >
        {/* Quatro colunas: os oito campos cabem em 4 + 4 dentro da largura da folha. As datas
            tinham folga de sobra na largura anterior (achado do Hilton na tela). */}
        <div
          className="c4u-filters__fields"
          style={{ gridTemplateColumns: "repeat(4, minmax(140px, 1fr))" }}
        >
        <TextField
          size="small"
          type="date"
          label="Data inicial"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ backgroundColor: '#fff' }}
        />
        <TextField
          size="small"
          type="date"
          label="Data final"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ backgroundColor: '#fff' }}
        />

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Formato</InputLabel>
          <Select
            value={layout}
            input={<OutlinedInput notched label="Formato" />}
            onChange={e => setLayout(e.target.value as 'por-profissional' | 'corrido')}
          >
            <MenuItem value="por-profissional">Um profissional por folha</MenuItem>
            <MenuItem value="corrido">Sem separação (corrido)</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Nome do Profissional</InputLabel>
          <Select
            multiple
            displayEmpty
            value={selectedIds}
            input={<OutlinedInput notched label="Nome do Profissional" />}
            renderValue={() => {
              if (selectedIds.length === 0) return 'Nenhum selecionado';
              if (selectedIds.length === allProfessionals.length) return 'Todos';
              return selectedIds.length + ' selecionados';
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          >
            <MenuItem onClick={alternarTodos}>
              <Checkbox
                checked={selectedIds.length === allProfessionals.length && allProfessionals.length > 0}
                indeterminate={selectedIds.length > 0 && selectedIds.length < allProfessionals.length}
              />
              <ListItemText primary="Todos" />
            </MenuItem>
            {allProfessionals.map(p => (
              <MenuItem key={p.id} value={p.id} onClick={() => alternarProfissional(p.id)}>
                <Checkbox checked={selectedIds.includes(p.id)} />
                <ListItemText primary={p.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Status de confirmação</InputLabel>
          <Select
            multiple
            displayEmpty
            value={selectedConfirmations}
            input={<OutlinedInput notched label="Status de confirmação" />}
            renderValue={() => resumoSelecao(selectedConfirmations, confirmationNames)}
            MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          >
            <MenuItem onClick={() => alternarTodosStatus(setSelectedConfirmations, confirmationNames)}>
              <Checkbox
                checked={selectedConfirmations.length === confirmationNames.length && confirmationNames.length > 0}
                indeterminate={selectedConfirmations.length > 0 && selectedConfirmations.length < confirmationNames.length}
              />
              <ListItemText primary="Todos" />
            </MenuItem>
            {confirmationNames.map(nome => (
              <MenuItem key={nome} value={nome} onClick={() => alternarStatus(setSelectedConfirmations, nome)}>
                <Checkbox checked={selectedConfirmations.includes(nome)} />
                <ListItemText primary={rotuloStatus(nome)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Status de presença</InputLabel>
          <Select
            multiple
            displayEmpty
            value={selectedPresences}
            input={<OutlinedInput notched label="Status de presença" />}
            renderValue={() => resumoSelecao(selectedPresences, presenceNames)}
            MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          >
            <MenuItem onClick={() => alternarTodosStatus(setSelectedPresences, presenceNames)}>
              <Checkbox
                checked={selectedPresences.length === presenceNames.length && presenceNames.length > 0}
                indeterminate={selectedPresences.length > 0 && selectedPresences.length < presenceNames.length}
              />
              <ListItemText primary="Todos" />
            </MenuItem>
            {presenceNames.map(nome => (
              <MenuItem key={nome} value={nome} onClick={() => alternarStatus(setSelectedPresences, nome)}>
                <Checkbox checked={selectedPresences.includes(nome)} />
                <ListItemText primary={rotuloStatus(nome)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Bloqueios de agenda</InputLabel>
          <Select
            value={blocksFilter}
            input={<OutlinedInput notched label="Bloqueios de agenda" />}
            onChange={e => setBlocksFilter(e.target.value as 'mostrar' | 'ocultar')}
          >
            <MenuItem value="mostrar">Mostrar</MenuItem>
            <MenuItem value="ocultar">Ocultar</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth sx={{ minWidth: 0, backgroundColor: '#fff' }}>
          <InputLabel shrink>Paciente</InputLabel>
          <Select
            value={patientFilter}
            input={<OutlinedInput notched label="Paciente" />}
            onChange={e => setPatientFilter(e.target.value as 'todos' | 'definido' | 'sem')}
          >
            <MenuItem value="todos">Todos</MenuItem>
            <MenuItem value="definido">Definido</MenuItem>
            <MenuItem value="sem">Sem paciente</MenuItem>
          </Select>
        </FormControl>

        {/* Oito campos fecham duas linhas cheias de quatro; modelo e Imprimir vêm embaixo,
            dentro da grade (a faixa de ações à direita roubava largura — achado do Hilton). */}
        <PrintTemplateBar choice={templateChoice} sx={{ gridColumn: '1 / -1' }} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          disabled={loading || blocos.length === 0}
          sx={{
            backgroundColor: colors.primary, textTransform: 'none', height: 40,
            alignSelf: 'end', '&:hover': { backgroundColor: '#029AA8' },
          }}
        >
          Imprimir
        </Button>

        </div>

      </Box>

      <Box
        className="schedule-print-page"
        sx={{
          width: '210mm',
          minHeight: '297mm',
          padding: '15mm',
          paddingBottom: '30mm',
          boxSizing: 'border-box',
          background: 'white',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 0 8px rgba(0,0,0,0.15)',
          fontSize: '12pt',
          color: '#000',
          '@media print': {
            boxShadow: 'none', width: 'auto', minHeight: 'auto', margin: 0,
            padding: 0, fontSize: '12pt',
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : error ? (
          <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>{error}</Typography>
        ) : blocos.length === 0 ? (
          <Typography sx={{ color: '#6c757d', textAlign: 'center', py: 8, fontSize: '11pt' }}>
            Nenhum agendamento nem bloqueio no período, profissionais e situações selecionados.
          </Typography>
        ) : layout === 'corrido' ? (
          /* Uma moldura so para tudo: o cabecalho da clinica se repete por PAGINA, nao por
             profissional. Com N molduras, quem terminasse no meio da folha faria o proximo
             comecar com outro cabecalho no meio da mesma pagina. */
          <table className="print-frame">
            <thead><tr><td>{cabecalhoClinica()}</td></tr></thead>
            <tfoot><tr><td>{rodapeClinica()}</td></tr></tfoot>
            <tbody>
              {blocos.map(bloco => (
                <tr key={bloco.name}><td>{secaoProfissional(bloco)}</td></tr>
              ))}
              <tr><td>{emitidoEm()}</td></tr>
            </tbody>
          </table>
        ) : (
          blocos.map(bloco => (
            /* Uma MOLDURA COMPLETA por profissional, não um bloco solto dentro de uma célula:
               a quebra de página forçada dentro de `<td>` é inconsistente entre navegadores, e
               a folha de cada profissional é entregue a ele — precisa do cabeçalho da clínica
               em toda página, não só na primeira do documento. */
            <table className="print-frame" key={bloco.name}>
              <thead><tr><td>{cabecalhoClinica()}</td></tr></thead>
              <tfoot><tr><td>{rodapeClinica()}</td></tr></tfoot>
              <tbody><tr><td>
                {secaoProfissional(bloco)}
                {emitidoEm()}
              </td></tr></tbody>
            </table>
          ))
        )}
      </Box>
    </Box>
  );
};

export default SchedulePrint;
